<script setup>
import { ref } from 'vue'
import { useForm } from '@inertiajs/inertia-vue3'
import Toggle from '@/Shared/VisualComponents/Toggle.vue'
import RoundArrowDownOrange from '@/images/RoundArrowDownOrange'

// https://www.npmjs.com/package/vue-recaptcha-v3
import { useReCaptcha } from 'vue-recaptcha-v3'
const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()
const recaptcha = async () => {
	// (optional) Wait until recaptcha has been loaded.
	await recaptchaLoaded()

	// Execute reCAPTCHA with action "login".
	let token = await executeRecaptcha('login')

	return token
}

const props = defineProps({
	membershipTiers: {
		type: Array,
		required: true,
	},
	paymentFrequencies: {
		type: Array,
		required: true,
	},
	fields: {
		required: true
	},
	disabled: {
		type: Boolean,
		default: false
	}
})

const form = useForm(props.fields)

const submitBtn = ref(null)
function submit() {
	submitBtn.value.waitFor(
		new Promise((resolve) => {
			recaptcha().then((token) => {
				form.transform((data) => ({
					...data,
					recaptcha_token: token,
				}))
				.post(route('membership-applications.store'), {
					onFinish: () => {
						resolve()
					},
				})
			})
		})
	)
}

</script>

<template>
	<div class="max-w-4xl mx-auto">
		<slot name="header">
			<div class="flex flex-row content-center">
				<RoundArrowDownOrange class="w-10 h-8 pr-3" />
				<Heading size="2" class="p-0">Become a Member</Heading>
			</div>
			<small><a href="https://www.advancecairns.com/">Return to website</a></small>
		</slot>
	</div>
	<SectionCard class="max-w-4xl mx-auto">
		<form @submit.stop.prevent="addPaymentMethod" data-testid="membership-application-form">
			<input class="hidden" type="submit" value="Submit" />
			<h1 class="text-Blue text-xl font-semibold mb-2">Membership Application</h1>
			<h2 class="mb-2 font-semibold text-Orange">Company/Organisation Details</h2>
			<Input
				placeholder="eg. Advance Cairns"
				v-model="form.name"
				:errors="form.errors.name"
				:autofocus="true"
				label="Organisation Name"
				testid="name"
				:disabled="props.disabled"
        	/>
			<Input
				placeholder="eg. Advance Cairns"
				v-model="form.name_publication"
				:errors="form.errors.name_publication"
				label="Organisation Name for publication (if different)"
				testid="name_publication"
				:disabled="props.disabled"
        	/>
		 	<Input
                placeholder="eg. 123 Main St"
                v-model="form.street_address"
                :errors="form.errors.street_address"
                label="Street Address"
				testid="street_address"
				:disabled="props.disabled"
            />

			<div class="grid sm:grid-cols-3 gap-x-2">
				<Input
					placeholder="eg. Sydney"
					v-model="form.city"
					:errors="form.errors.city"
					label="City"
					testid="city"
					:disabled="props.disabled"
				/>
				<Input
					placeholder="eg. QLD"
					v-model="form.state"
					:errors="form.errors.state"
					label="State"
					type="state"
					testid="state"
					:disabled="props.disabled"
				/>
				<Input
					placeholder="eg. 2000"
					v-model="form.postal_code"
					:errors="form.errors.postal_code"
					label="Postal Code"
					type="post_code"
					testid="postal_code"
					:disabled="props.disabled"
				/>
			</div>

			<Input
				placeholder="eg. https://www.advancecairns.com.au"
				v-model="form.website"
				:errors="form.errors.website"
				label="Website"
				type="website"
				testid="website"
				:disabled="props.disabled"
			/>
			<div class="grid sm:grid-cols-2  gap-x-2">
				<Input
					placeholder="eg. +61 4 1234 5678"
					v-model="form.phone"
					:errors="form.errors.phone"
					label="Best Phone"
					type="phone"
					testid="phone"
					:disabled="props.disabled"
				/>
				<Input
					placeholder="eg. business@website.com"
					v-model="form.email"
					:errors="form.errors.email"
					label="Business Email"
					type="email"
					testid="email"
					:disabled="props.disabled"
				/>
			</div>

			<div class="grid sm:grid-cols-2 gap-x-2">
				<Input
                    placeholder="eg. 012345678"
                    v-model="form.abn"
                    :errors="form.errors.abn"
                    label="ABN"
					type="abn"
					testid="abn"
					:disabled="props.disabled"
                />
				<Input
                    placeholder="eg. Health"
                    v-model="form.industry_sector"
                    :errors="form.errors.industry_sector"
                    label="Industry Sector"
					testid="industry_sector"
					:disabled="props.disabled"
                />
			</div>

			<h2 class="mt-4 mb-2 font-semibold text-Orange">Company/Organisation Contacts</h2>
			<h3 class="font-medium text-sm">Nominated Representative (for official purposes)</h3>
			<div class="grid sm:grid-cols-2 gap-x-2">
				<Input
					label="Name"
					placeholder="eg. Linda Smith"
					v-model="form.contact_representative_name"
					:errors="form.errors.contact_representative_name"
					testid="contact_representative_name"
					:disabled="props.disabled"
				/>
				<Input
					label="Position Title"
					placeholder="eg. CEO"
					v-model="form.contact_representative_position"
					:errors="form.errors.contact_representative_position"
					testid="contact_representative_position"
					:disabled="props.disabled"
				/>
				<Input
					label="Phone"
					placeholder="eg. +61 4 1234 5678"
					v-model="form.contact_representative_phone"
					:errors="form.errors.contact_representative_phone"
					type="phone"
					testid="contact_representative_phone"
					:disabled="props.disabled"
				/>
				<Input
					label="Email"
					placeholder="eg. linda@advancecairns.com.au"
					v-model="form.contact_representative_email"
					:errors="form.errors.contact_representative_email"
					testid="contact_representative_email"
					:disabled="props.disabled"
				/>
			</div>

			<h3 class="mt-4 font-medium text-sm">Office Contact (for newsletter distribution/invitations etc)</h3>
			<div class="grid sm:grid-cols-2 gap-x-2">
				<Input
					label="Name"
					placeholder="eg. Darren Clerk"
					v-model="form.contact_office_name"
					:errors="form.errors.contact_office_name"
					testid="contact_office_name"
					:disabled="props.disabled"
				/>
				<Input
					label="Position Title"
					placeholder="eg. Admin"
					v-model="form.contact_office_position"
					:errors="form.errors.contact_office_position"
					testid="contact_office_position"
					:disabled="props.disabled"
				/>
				<Input
					label="Phone"
					placeholder="eg. +61 4 1234 5678"
					v-model="form.contact_office_phone"
					:errors="form.errors.contact_office_phone"
					testid="contact_office_phone"
					type="phone"
					:disabled="props.disabled"
				/>
				<Input
					label="Email"
					placeholder="eg. admin@advancecairns.com.au"
					v-model="form.contact_office_email"
					:errors="form.errors.contact_office_email"
					testid="contact_office_email"
					:disabled="props.disabled"
				/>
			</div>

			<h2 class="mt-4 mb-2 font-semibold text-Orange">Payment Details</h2>
			<div class="grid sm:grid-cols-2 gap-2">
				<Input
					label="Preferred Membership Level"
					type="select"
					v-model="form.membership_level"
					:options="membershipTiers"
					:errors="form.errors.membership_level"
					testid="membership_level"
					:disabled="props.disabled"
				/>
				<Input
					label="Payment Frequency"
					type="select"
					v-model="form.payment_frequency"
					:options="paymentFrequencies"
					:errors="form.errors.payment_frequency"
					selected="please select"
					testid="payment_frequency"
					:disabled="props.disabled"
				/>
			</div>

			<label class="flex justify-end text-sm mt-2 -mr-2">
				I have read and agree to the terms and conditions of membership
				<Input
					type="checkbox"
					class="ml-2"
					v-model="form.agree_to_terms"
					testid="agree"
					:errors="form.errors.agree_to_terms"
					:disabled="props.disabled"
				/>
			</label>
		</form>

		<template #footer>
			<slot name="footer">
				<PromiseButton
					ref="submitBtn"
					testid="membership-application-submit"
					color="blue"
					@click="submit"
					:disabled="props.disabled"
				>
					Proceed to Payment
				</PromiseButton>
			</slot>
		</template>
	</SectionCard>
</template>

